import { createContext, useContext, useState, useCallback, useEffect, type ReactNode } from 'react'
import { setApiErrorHandler, type ApiError } from '../api/client'

interface ToastItem {
  id: number
  message: string
  type: 'error' | 'warning' | 'info'
}

interface ToastContextValue {
  addToast: (message: string, type?: ToastItem['type']) => void
}

const ToastContext = createContext<ToastContextValue>({ addToast: () => {} })

export const useToast = () => useContext(ToastContext)

let nextId = 0

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastItem[]>([])

  const addToast = useCallback((message: string, type: ToastItem['type'] = 'error') => {
    const id = ++nextId
    setToasts(prev => [...prev.slice(-4), { id, message, type }])
    setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), 8000)
  }, [])

  const dismiss = useCallback((id: number) => {
    setToasts(prev => prev.filter(t => t.id !== id))
  }, [])

  useEffect(() => {
    setApiErrorHandler((err: ApiError) => {
      addToast(err.message, err.status === 401 ? 'warning' : 'error')
    })
    return () => setApiErrorHandler(null as never)
  }, [addToast])

  return (
    <ToastContext.Provider value={{ addToast }}>
      {children}
      {toasts.length > 0 && (
        <div className="smm-toast-container">
          {toasts.map(t => (
            <div key={t.id} className={`smm-toast smm-toast-${t.type}`}>
              <span>{t.message}</span>
              <button className="smm-toast-close" onClick={() => dismiss(t.id)}>&times;</button>
            </div>
          ))}
        </div>
      )}
    </ToastContext.Provider>
  )
}
